import React, { useState, useEffect, useCallback } from 'react';
import { Unit, CreateUnit, UpdateUnit } from '../types/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { apiService } from '../services/api';
import SearchBar from './SearchBar';
import { appColors } from '../theme/colors';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';
import {
  ListPageRoot,
  ListPageToolbar,
  ListPageTableSurface,
  ListPageContentSection,
  ListPageMobileStack,
  ListPageTableHeadRow,
  ListPageTh,
  listPageTableBodyClass,
  listPageTableRowClass,
} from './listPage';

interface UnitsTabProps {
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  onRegisterCreateAction?: (handler: (() => void) | null) => void;
}

const UnitsTab: React.FC<UnitsTabProps> = ({ onError, onSuccess, onRegisterCreateAction }) => {
  const { user } = useAuth();
  const { confirmDelete, deleteConfirmationDialog } = useDeleteConfirmation();
  const canCreate = hasPermission(user, permissions.masterData.create);
  const canEdit = hasPermission(user, permissions.masterData.edit);
  const canDelete = hasPermission(user, permissions.masterData.delete);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  
  const [formData, setFormData] = useState<CreateUnit>({
    unitCode: '',
    unitName: '',
    unitSymbol: '',
    unitType: '',
    displayOrder: undefined,
    isActive: true,
  });

  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiService.getAllUnits(includeInactive);
      // Filter by search term client-side
      let filtered = result;
      if (searchTerm) {
        filtered = result.filter(u => 
          u.unitCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.unitName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (u.unitSymbol && u.unitSymbol.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (u.unitType && u.unitType.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      setUnits(filtered);
    } catch (err: any) {
      onError('Failed to fetch units. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onError, includeInactive, searchTerm]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  const handleOpenDialog = (unit?: Unit) => {
    if (unit) {
      setEditingUnit(unit);
      setFormData({
        unitCode: unit.unitCode,
        unitName: unit.unitName,
        unitSymbol: unit.unitSymbol || '',
        unitType: unit.unitType || '',
        displayOrder: unit.displayOrder || undefined,
        isActive: unit.isActive,
      });
    } else {
      setEditingUnit(null);
      setFormData({
        unitCode: '',
        unitName: '',
        unitSymbol: '',
        unitType: '',
        displayOrder: undefined,
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUnit(null);
    setFormData({
      unitCode: '',
      unitName: '',
      unitSymbol: '',
      unitType: '',
      displayOrder: undefined,
      isActive: true,
    });
  };

  const handleSubmit = async (event: React.MouseEvent) => {
    event.preventDefault();
    try {
      if (editingUnit) {
        if (!canEdit) {
          onError('You do not have permission to edit units.');
          return;
        }
        const updateData: UpdateUnit = {
          unitCode: formData.unitCode,
          unitName: formData.unitName,
          unitSymbol: formData.unitSymbol || undefined,
          unitType: formData.unitType || undefined,
          displayOrder: formData.displayOrder,
          isActive: formData.isActive,
        };
        await apiService.updateUnit(editingUnit.unitId, updateData);
        onSuccess('Unit updated successfully!');
      } else {
        if (!canCreate) {
          onError('You do not have permission to create units.');
          return;
        }
        await apiService.createUnit(formData);
        onSuccess('Unit created successfully!');
      }

      handleCloseDialog();
      fetchUnits();
    } catch (err: any) {
      onError(err.response?.data || 'Operation failed. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!canDelete) {
      onError('You do not have permission to delete units.');
      return;
    }
    const confirmed = await confirmDelete({ message: 'Are you sure you want to delete this unit?' });
    if (!confirmed) return;
    try {
      await apiService.deleteUnit(id);
      onSuccess('Unit deleted successfully!');
      fetchUnits();
    } catch (err: any) {
      onError(err.response?.data || 'Failed to delete unit.');
    }
  };

  useEffect(() => {
    onRegisterCreateAction?.(canCreate ? () => handleOpenDialog() : null);
    return () => onRegisterCreateAction?.(null);
  }, [onRegisterCreateAction, canCreate]);

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? 'text-green-300 bg-green-900/50'
      : 'text-red-300 bg-red-900/50';
  };

  const getStatusDot = (isActive: boolean) => {
    return isActive ? 'bg-green-500' : 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <CircularProgress />
      </div>
    );
  }

  return (
    <ListPageRoot>
      <ListPageToolbar className="flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:flex-1 sm:min-w-0">
          <SearchBar
            variant="listPage"
            placeholder="Search units..."
            value={searchTerm}
            onChange={setSearchTerm}
            className="w-full sm:max-w-md"
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: appColors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: appColors.primary,
                  },
                }}
              />
            }
            label="Include Inactive"
            sx={{ color: 'white' }}
          />
        </div>
      </ListPageToolbar>

      <ListPageContentSection>
        <ListPageTableSurface>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <ListPageTableHeadRow>
                  <ListPageTh>ID</ListPageTh>
                  <ListPageTh>Code</ListPageTh>
                  <ListPageTh>Name</ListPageTh>
                  <ListPageTh>Symbol</ListPageTh>
                  <ListPageTh className="hidden lg:table-cell">Type</ListPageTh>
                  <ListPageTh className="text-center">Display Order</ListPageTh>
                  <ListPageTh className="text-center">Status</ListPageTh>
                  <ListPageTh className="hidden lg:table-cell">Created</ListPageTh>
                  <ListPageTh className="text-right">Actions</ListPageTh>
                </ListPageTableHeadRow>
              </thead>
              <tbody className={listPageTableBodyClass}>
                {units.map((unit) => (
                  <tr key={unit.unitId} className={listPageTableRowClass}>
                    <td className="px-5 py-4 text-slate-300">#{unit.unitId}</td>
                    <td className="px-5 py-4 text-white font-medium">{unit.unitCode}</td>
                    <td className="px-5 py-4 text-white font-medium">{unit.unitName}</td>
                    <td className="px-5 py-4 text-slate-200">{unit.unitSymbol || '—'}</td>
                    <td className="px-5 py-4 text-slate-400 hidden lg:table-cell">{unit.unitType || '—'}</td>
                    <td className="px-5 py-4 text-center text-slate-300">{unit.displayOrder ?? '—'}</td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(unit.isActive)}`}>
                        <span className={`w-2 h-2 mr-2 rounded-full ${getStatusDot(unit.isActive)}`} />
                        {unit.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-300 hidden lg:table-cell">
                      {unit.createdAt ? new Date(unit.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="action-row-icon-buttons">
                        {canEdit && (
                          <button
                            type="button"
                            className="text-primary hover:bg-primary/10"
                            aria-label="Edit unit"
                            onClick={() => handleOpenDialog(unit)}
                          >
                            <span className="material-symbols-outlined text-base">edit</span>
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="text-red-400 hover:bg-red-500/10"
                            aria-label="Delete unit"
                            onClick={() => handleDelete(unit.unitId)}
                          >
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ListPageTableSurface>

        <ListPageMobileStack>
          {units.map((unit) => (
            <div
              key={unit.unitId}
              className="bg-elevation-dark rounded-xl p-4 border border-border-dark shadow-lg flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="material-symbols-outlined text-base text-primary shrink-0">straighten</span>
                    <h3 className="text-white font-semibold text-lg">{unit.unitName}</h3>
                    <span className="text-xs text-slate-500">#{unit.unitId}</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-1">Code: {unit.unitCode}</p>
                  {unit.unitSymbol && <p className="text-slate-300 text-sm mb-1">Symbol: {unit.unitSymbol}</p>}
                  {unit.unitType && <p className="text-slate-300 text-sm">Type: {unit.unitType}</p>}
                </div>
                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full shrink-0 ${getStatusColor(unit.isActive)}`}>
                  <span className={`w-2 h-2 mr-1 rounded-full ${getStatusDot(unit.isActive)}`} />
                  {unit.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400">
                {unit.displayOrder != null && (
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm text-primary">sort</span>
                    Order: {unit.displayOrder}
                  </span>
                )}
                {unit.createdAt && <span>{new Date(unit.createdAt).toLocaleDateString()}</span>}
              </div>

              <div className="action-row-icon-buttons border-t border-border-dark/50 pt-3">
                {canEdit && (
                  <button
                    type="button"
                    className="text-primary hover:bg-primary/10"
                    onClick={() => handleOpenDialog(unit)}
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="text-red-400 hover:bg-red-500/10"
                    onClick={() => handleDelete(unit.unitId)}
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </ListPageMobileStack>
      </ListPageContentSection>

      {/* Unit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm" 
        fullWidth
        PaperProps={listPageDialogPaperProps}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
          {editingUnit ? `Edit Unit - ${editingUnit.unitName}` : 'Create New Unit'}
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pt: '20px' }}>
          <div className="space-y-4">
            <TextField
              autoFocus
              margin="dense"
              label="Unit Code"
              fullWidth
              variant="outlined"
              value={formData.unitCode}
              onChange={(e) => setFormData({ ...formData, unitCode: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Unit Name"
              fullWidth
              variant="outlined"
              value={formData.unitName}
              onChange={(e) => setFormData({ ...formData, unitName: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Unit Symbol"
              fullWidth
              variant="outlined"
              value={formData.unitSymbol}
              onChange={(e) => setFormData({ ...formData, unitSymbol: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Unit Type"
              fullWidth
              variant="outlined"
              value={formData.unitType}
              onChange={(e) => setFormData({ ...formData, unitType: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Display Order"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.displayOrder || ''}
              onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value ? parseInt(e.target.value) : undefined })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: appColors.primary,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: appColors.primary,
                    },
                  }}
                />
              }
              label="Active"
              sx={{ color: 'white' }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ 
              color: appColors.mutedText, 
              borderColor: appColors.dialogBorder,
              '&:hover': {
                borderColor: appColors.dialogBorder,
                color: appColors.primary,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              backgroundColor: appColors.primary,
              '&:hover': {
                backgroundColor: appColors.primary,
                opacity: 0.9,
              }
            }}
          >
            {editingUnit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {deleteConfirmationDialog}
    </ListPageRoot>
  );
};

export default UnitsTab;

